import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { EVO_BODY_PADDING, EVO_BODY_PADDING_WIDE } from '@/app/components/search/components/Modal/OpenXModal/utils.ts';
import TransportJourneyDetails from '@/app/components/hotel-detail/flight-train/TransportJourneyDetails';
import { EvoTransportJourneyDetails } from '@/app/components/hotel-detail/evo/EvoTransportJourneyDetails';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { BottomContinuePanel } from '@/app/components/hotel-detail/BottomContinuePanel';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import type { BrandedFare } from '@/app/api/hotel-detail/branded-fares/route';
import ModalHeader from '../../search/components/Modal/LayoutElements/ModalHeader';
import ModalStickyFooter from '../../search/components/Modal/LayoutElements/ModalStickyFooter';
import OpenXModal from '../../search/components/Modal/OpenXModal';
import { useTranslation } from '../../translations/Translation';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useChangeTransportDetailsModal } from './ChangeTransportDetailsModalProvider';
import customCss from '../css/customCss';
import { useIsDesktop } from '../../search/utils/useSsrMedia';
import { useChangeTransportHandler } from './hooks/useChangeTransportHandler';

type ChangeTransportDetailsModalProps = {
  onChangeTransport: (id: string, transportClass: string) => void;
} & React.HTMLAttributes<HTMLElement>;

// MPH-474: on Evo this modal opens on top of the change-flight modal (#flight-change-modal). The
// design-system Dialog hardcodes overlay z-index 1300 / paper 1400, so two stacked dialogs collide:
// this modal's scrim (1300) renders below the change-flight paper (1400) and the panel isn't dimmed.
// When stacked, raise this paper above it and render our own full-viewport backdrop (matches how
// EvoFilterModal stacks above the same modal). Standalone usages (non-Evo page, package review) are
// unaffected because #flight-change-modal is absent.
const STACKED_MODAL_Z_INDEX = 1500;

const StackedBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundOverlay};
  z-index: ${STACKED_MODAL_Z_INDEX - 50};
`;

export function ChangeTransportDetailsModal(props: ChangeTransportDetailsModalProps) {
  const { onChangeTransport } = props;
  const { isOpen, selectedTransport, closeModal: closeDetailsModal } = useChangeTransportDetailsModal();
  const { selectedMealPlan, selectedBrandedFare: pageBrandedFare } = useSelectedMealPlan();
  // Off Evo the fare is chosen inside this modal; on Evo it is chosen on the flight page, so the
  // price here has to read the page's fare or the footer would contradict the page's CTA. The
  // transportId guard below still keeps it off the cards for other flights.
  const [modalBrandedFare, setModalBrandedFare] = useState<BrandedFare | undefined>(undefined);
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  // MPH-833: one flag for the whole modal, read from the same place ModalHeader and
  // ModalStickyFooter read it, so the chrome and the body can never end up on different arms.
  const { isEvoLayout } = useCommonClient();
  const { isTrain, totalGuestsWithoutInfants } = useUrlSearchParams();
  const isDesktop = useIsDesktop();
  // MPH-833: off Evo the price waits for the branded fares to land; the Evo body has no fares to
  // wait for (the fare is chosen on the flight page), so the price is there from the start.
  const [showPrice, setShowPrice] = useState<boolean>(isTrain || isEvoLayout);
  const { handleContinueClick } = useChangeTransportHandler(onChangeTransport);

  const onBrandedFaresLoaded = () => {
    setShowPrice(true);
  };

  const onSelectBrandedFare = (brandedFare: BrandedFare | undefined) => {
    setModalBrandedFare(brandedFare);
  };

  useEffect(() => {
    if (isTrain) {
      setShowPrice(true);
    }
  }, [isTrain]);

  useEffect(() => {
    if (modalBrandedFare && selectedTransport && modalBrandedFare.transportId !== selectedTransport.id) {
      setModalBrandedFare(undefined);
    }
  }, [selectedTransport]);

  const selectedBrandedFare = isEvoLayout ? pageBrandedFare : modalBrandedFare;
  const isBrandedFareForCurrentTransport =
    !!selectedBrandedFare && selectedBrandedFare.transportId === selectedTransport?.id;
  const finalPrice = isBrandedFareForCurrentTransport
    ? selectedBrandedFare.finalPrice
    : selectedMealPlan
      ? selectedMealPlan.finalPrice + (selectedTransport?.priceIncrement ?? 0)
      : 0;
  const finalTotalPrice = finalPrice * totalGuestsWithoutInfants;
  // Branded fares expose only `priceDifference` (delta vs. base meal plan), not a separate
  // pre-tax price. We reuse it here under the assumption that the branded-fare uplift does
  // not introduce its own tax delta — i.e. the with-tax and without-tax deltas are equal.
  const priceWithoutTaxes = selectedMealPlan
    ? selectedMealPlan.priceWithoutTaxes +
      (isBrandedFareForCurrentTransport
        ? selectedBrandedFare.priceDifference
        : (selectedTransport?.priceIncrement ?? 0))
    : 0;
  const title = isTrain
    ? t('vacation.common.trains.trainandbaggagedetails')
    : t('vacation.common.flights.flightandbaggagedetails');

  // MPH-474: elevate above the change-flight modal only while it is actually open behind us.
  const isStackedOverChangeFlight =
    !!isOpen && typeof document !== 'undefined' && !!document.getElementById('flight-change-modal');

  return (
    <>
      {isStackedOverChangeFlight &&
        ReactDOM.createPortal(<StackedBackdrop aria-hidden="true" onClick={closeDetailsModal} />, document.body)}
      <OpenXModal
        isOpen={isOpen || false}
        isMobile={!isDesktop}
        onClose={closeDetailsModal}
        fullScreen={false}
        brand={localization.brand as Brand}
        disableBrowserHistoryNavigation={true}
        id="flight-details-modal"
        size={isEvoLayout ? MODAL_SIZES.L_EVO : MODAL_SIZES.XL}
        aria-label={title}
        bodyBackgroundColor={isEvoLayout ? 'backgroundLayer02' : 'backgroundLayer01'}
        // On Evo the standard Evo insets: 24px on desktop, 16px once the modal is a full-width
        // sheet — the same pair StickyFooterContainer keys off, so on Evo the CTA below lines up with
        // the body at both widths without this modal adding anything of its own (MPH-1116). The
        // legacy arm still tops the panel up itself; see the css prop on BottomContinuePanel.
        bodyPadding={isEvoLayout ? EVO_BODY_PADDING_WIDE : 3}
        bodyPaddingS={isEvoLayout ? EVO_BODY_PADDING : 3}
        bodyPaddingXS={isEvoLayout ? EVO_BODY_PADDING : 3}
        {...(isStackedOverChangeFlight ? { style: { zIndex: STACKED_MODAL_Z_INDEX } } : {})}
        // MPH-833: ModalHeader switches to the Evo header (close top-right, scroll-driven shadow)
        // while keeping the legacy one off Evo, and scrollShadows is what silences the design
        // system's own always-on header shadow. It also strips the DS footer's padding and shadow,
        // hence ModalStickyFooter below — a passthrough outside Evo.
        scrollShadows={isEvoLayout}
        header={{
          sticky: true,
          element: <ModalHeader handleClose={closeDetailsModal}>{title}</ModalHeader>,
        }}
        {...(selectedTransport
          ? {
              footer: {
                sticky: true,
                element: selectedMealPlan && (
                  <ModalStickyFooter>
                    <BottomContinuePanel
                      // Off Evo, ModalStickyFooter is a passthrough and OpenXModal pins the design-system
                      // footer to 16px, so this 8px is what brings the CTA onto the body's 24px inset from
                      // 744px up. On Evo the sticky footer supplies the full inset itself (MPH-1116).
                      css={
                        isEvoLayout
                          ? undefined
                          : css`
                              @media ${customCss.breakpoints.sm} {
                                padding: 0px 8px;
                              }
                            `
                      }
                      selectedMealPlan={{
                        ...selectedMealPlan,
                        finalPrice,
                        finalTotalPrice,
                        priceWithoutTaxes,
                      }}
                      onContinueClick={() => {
                        handleContinueClick({
                          transportId: selectedTransport.id,
                          transportClass: getUniqueSortedTransportClasses(
                            selectedTransport.outbound,
                            selectedTransport.inbound
                          ),
                          brandedFareId: selectedBrandedFare?.fareId,
                        });
                      }}
                      showPrice={showPrice || !!selectedBrandedFare}
                      // Figma 979-40680 / MPH-846: on Evo every sheet CTA is the 48px-tall (large) button, so
                      // this one matches the room and cancellation sheets. No wideButton: this footer insets the
                      // whole panel instead (the sticky footer on Evo, the css prop above off it), where the room
                      // and cancellation sheets widen the button itself.
                      buttonSize={isEvoLayout ? 'large' : 'regular'}
                    />
                  </ModalStickyFooter>
                ),
              },
            }
          : undefined)}
      >
        {selectedTransport &&
          (isEvoLayout ? (
            // Figma 1435-46234: the same body as the flight page's own details modal — route title,
            // outbound/inbound cards with their icons, luggage and CO2 — and no fare selector: in Evo
            // the fare is picked on the flight page, after the flight is chosen.
            <EvoTransportJourneyDetails
              outbound={selectedTransport.outbound}
              inbound={selectedTransport.inbound}
              co2Footprint={selectedTransport.co2Footprint}
              numberOfBaggagePiecesIncluded={selectedTransport.numberOfBaggagePiecesIncluded}
            />
          ) : (
            <TransportJourneyDetails
              outbound={selectedTransport.outbound}
              inbound={selectedTransport.inbound}
              co2Footprint={selectedTransport.co2Footprint}
              transportId={selectedTransport.id}
              priceIncrement={selectedTransport.priceIncrement ?? 0}
              eligibleBrandedFares={selectedTransport.eligibleBrandedFares}
              numberOfBaggagePiecesIncluded={selectedTransport.numberOfBaggagePiecesIncluded}
              css={css`
                [data-testid*='info-container']:not(.keep-visible) {
                  display: none;
                }
                [class*='JourneyDetailsWayIntro__WayIntroTypeStyed'] {
                  display: none;
                }
              `}
              onLoadFare={onBrandedFaresLoaded}
              onSelectBrandedFare={onSelectBrandedFare}
            />
          ))}
      </OpenXModal>
    </>
  );
}

export const getUniqueSortedTransportClasses = (
  outbound: TransportBoundType | undefined,
  inbound: TransportBoundType | undefined
) => {
  const getUniqueTransportClasses = (bound: TransportBoundType | undefined) =>
    bound?.segments.reduce((acc: string[], segment: TransportSegment) => {
      acc.push(segment.classAvailability);
      return acc;
    }, []) ?? [];
  const uniqueTransportClasses = new Set([
    ...getUniqueTransportClasses(outbound),
    ...getUniqueTransportClasses(inbound),
  ]);
  return [...uniqueTransportClasses].sort().join('');
};
